import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from "../assests/Untitled design.png";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Handle window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when pressing escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'fish-food', label: 'Fish Food', path: '/fish-food' },
    { id: 'fish', label: 'Fish', path: '/fish' },
    { id: 'about', label: 'About Us', path: '/about' },
    { id: 'contact', label: 'Contact', path: '/contact' }
  ];

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  // Get active tab based on current path
  const getActiveTab = () => {
    const currentPath = location.pathname;
    const activeItem = navItems.find(item => item.path === currentPath);
    return activeItem ? activeItem.id : 'home';
  };

  const activeTab = getActiveTab();

  // Icons for mobile bottom navigation
  const getIcon = (itemId, isActive = false) => {
    const iconColor = isActive ? '#1770b6' : '#9ca3af';
    
    switch(itemId) {
      case 'home':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 9L12 3L21 9L21 20H15V14H9V20H3V9Z" stroke={iconColor} strokeWidth="2" fill="none"/>
            <path d="M3 9L12 3L21 9" stroke={iconColor} strokeWidth="2" fill="none"/>
          </svg>
        );
      case 'fish-food':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C16.42 20 20 16.42 20 12" stroke={iconColor} strokeWidth="2" fill="none"/>
            <circle cx="12" cy="12" r="8" stroke={iconColor} strokeWidth="2" fill="none"/>
            <path d="M16 8L20 4M20 4L16 4M20 4L20 8" stroke={iconColor} strokeWidth="2"/>
          </svg>
        );
      case 'fish':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5L13.5 8.5L17 10L13.5 11.5L12 15L10.5 11.5L7 10L10.5 8.5L12 5Z" fill={iconColor}/>
            <path d="M5 15L8 17L5 19L3 17L5 15Z" fill={iconColor}/>
            <path d="M19 15L16 17L19 19L21 17L19 15Z" fill={iconColor}/>
          </svg>
        );
      case 'about':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke={iconColor} strokeWidth="2" fill="none"/>
            <path d="M12 8V12M12 16H12.01" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
      case 'contact':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 8L12 13L21 8M5 4H19C20.1 4 21 4.9 21 6V18C21 19.1 20.1 20 19 20H5C3.9 20 3 19.1 3 18V6C3 4.9 3.9 4 5 4Z" stroke={iconColor} strokeWidth="2" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  // Desktop Navigation
  const DesktopNav = () => (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer group">
            <div className="text-[#1770b6] transition-transform group-hover:scale-105">
              <img src={logo} alt='logo' className='h-16 w-auto'/>
            </div>
            <div className="flex flex-col">
              <div className="flex items-baseline space-x-1">
                <span className="text-2xl font-extrabold text-[#1770b6] tracking-tight">LION</span>
                <span className="text-lg font-extrabold text-gray-800">HEAD</span>
              </div>
              <span className="text-xs text-gray-500 font-medium">Premium Fish Food</span>
            </div>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`relative px-1 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-[#1770b6]'
                    : 'text-gray-700 hover:text-[#1770b6]'
                }`}
              >
                {item.label}
                {location.pathname === item.path && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1770b6] rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <Link 
            to="/fish-food" 
            className="hidden md:block bg-[#1770b6] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 hover:bg-[#0e5a94] hover:shadow-lg hover:shadow-[#1770b6]/30 transform hover:-translate-y-0.5"
          >
            Shop Now
          </Link>
        </div>
      </div>
    </nav>
  );

  // Mobile Navigation - Enhanced App-like Experience
  const MobileNav = () => (
    <>
      {/* Top Header */}
      <div className="md:hidden bg-white shadow-md sticky top-0 z-50">
        <div className="flex justify-between items-center px-4 py-3">
          <Link to="/" className="flex items-center gap-2" onClick={handleNavClick}>
            <img 
              src={logo} 
              alt='Lion Head' 
              className='h-10 w-auto rounded-lg object-contain'
            />
            <div className="flex flex-col">
              <h1 className="text-sm font-bold tracking-tight leading-tight">
                <span className="text-[#1770b6]">LION</span>
                <span className="text-gray-800"> HEAD</span>
              </h1>
              <p className="text-[8px] text-gray-500 uppercase tracking-wider">
                Premium Fish Food
              </p>
            </div>
          </Link>
          
          {/* Hamburger Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="w-10 h-10 rounded-full bg-gray-50 active:bg-gray-100 transition-all duration-200 flex items-center justify-center shadow-sm"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex flex-col justify-between items-center">
              <span className={`block h-0.5 bg-[#1770b6] rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
              }`} style={{ width: '20px' }} />
              <span className={`block h-0.5 bg-[#1770b6] rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? 'opacity-0' : ''
              }`} style={{ width: '20px' }} />
              <span className={`block h-0.5 bg-[#1770b6] rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
              }`} style={{ width: '20px' }} />
            </div>
          </button>
        </div>
      </div>

      {/* Slide-out Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="fixed right-0 top-0 bottom-0 w-80 bg-white shadow-2xl z-50 md:hidden animate-slide-in-right">
            <div className="flex flex-col h-full">
              {/* Menu Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src={logo} 
                    alt='Lion Head' 
                    className='h-12 w-auto rounded-lg'
                  />
                  <div>
                    <h2 className="font-bold text-gray-800 text-lg">Menu</h2>
                    <p className="text-xs text-gray-500">Choose your destination</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:scale-95 transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              
              {/* Navigation Links */}
              <div className="flex-1 overflow-y-auto py-4 px-4">
                {navItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={handleNavClick}
                    className={`w-full flex items-center gap-4 px-4 py-3 mb-2 rounded-xl transition-all duration-200 ${
                      location.pathname === item.path
                        ? 'bg-[#1770b6]/10 text-[#1770b6]'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      location.pathname === item.path ? 'bg-[#1770b6] text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {getIcon(item.id, location.pathname === item.path)}
                    </div>
                    <span className="flex-1 text-left font-medium">{item.label}</span>
                    {location.pathname === item.path && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    )}
                  </Link>
                ))}
              </div>
              
              {/* Shop Now Button */}
              <div className="p-4 border-t border-gray-100">
                <Link 
                  to="/fish-food" 
                  onClick={handleNavClick}
                  className="w-full bg-gradient-to-r from-[#1770b6] to-[#0e5a94] text-white py-3 rounded-xl font-semibold active:scale-95 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6H5L7 15H18L20 6H5" stroke="white" strokeWidth="2" fill="none"/>
                    <circle cx="9" cy="19" r="2" stroke="white" strokeWidth="2" fill="none"/>
                    <circle cx="17" cy="19" r="2" stroke="white" strokeWidth="2" fill="none"/>
                  </svg>
                  Shop Now
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Bottom Navigation Bar - App-like */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg z-40">
        <div className="flex justify-around items-center px-2 py-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              onClick={handleNavClick}
              className={`flex flex-col items-center py-1 px-2 rounded-lg transition-all duration-200 active:scale-95 ${
                location.pathname === item.path ? 'scale-105' : ''
              }`}
            >
              <div className="mb-1">
                {getIcon(item.id, location.pathname === item.path)}
              </div>
              <span className={`text-[10px] font-medium ${
                location.pathname === item.path ? 'text-[#1770b6]' : 'text-gray-400'
              }`}>
                {item.label}
              </span>
              {location.pathname === item.path && (
                <span className="absolute -top-1 w-1 h-1 bg-[#1770b6] rounded-full" />
              )}
            </Link>
          ))}
        </div>
      </div>

      {/* Add padding to prevent content from hiding behind bottom nav */}
      {/* <div className="pb-16 md:pb-0" /> */}
    </>
  );

  return (
    <>
      {isMobile ? <MobileNav /> : <DesktopNav />}
    </>
  );
};

export default Navbar;